import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, Check, CheckCircle2,
  Copy, FileText, Gamepad2, Loader2, RefreshCw, X, XCircle,
} from 'lucide-react';
import {
  formatDateTime, formatMoney, transactionStatusLabel, transactionTypeLabel,
} from '../lib/formatters';
import { turnoverView } from '../lib/principalTurnover';
import { AWAITING_APPROVAL, financeDecision } from '../lib/financeDecision';
import {
  copyToClipboard, payoutAccountView, transactionActor, transactionNoteLines,
} from '../lib/transactions';
import '../finance.css';

const iconMap = {
  deposit: ArrowDownCircle,
  withdraw: ArrowUpCircle,
};

const stateToneMap = {
  onaylandi: 'is-ok',
  beklemede: 'is-wait',
  reddedildi: 'is-bad',
};

// Anapara çevrimi rozetinin rengi; ölçünün metni ve tonu lib/principalTurnover
// içindedir, burada yalnızca bu sayfanın rozet sınıfına çevrilir.
const turnoverToneMap = {
  green: 'is-ok',
  red: 'is-bad',
  gray: 'is-gray',
};

// Sağlayıcıya iletilmeden önce yönetici onayı bekleyen çekimin durumu;
// karar kurallarıyla birlikte tek yerde tutulur.
export { AWAITING_APPROVAL };

// Sağlayıcı işlem durumunun panelde okunabilir karşılığı.
const providerStateLabel = {
  [AWAITING_APPROVAL]: 'Yönetici onayı bekliyor',
  creating: 'Sağlayıcıya iletiliyor',
  pending: 'Sağlayıcıda bekliyor',
  success: 'Sağlayıcıda başarılı',
  fail: 'Sağlayıcıda başarısız',
  failed: 'Sağlayıcı reddetti',
  not_found: 'Sağlayıcıda kayıt bulunamadı',
  expired: 'Ödeme süresi doldu',
  unknown: 'Sonuç belirsiz, mutabakat bekliyor',
};

const Section = ({ title, children }) => (
  <section className="fin-section">
    <h3>{title}</h3>
    {children}
  </section>
);

// Kopyalanabilir kod alanı (IBAN, cüzdan adresi, işlem kimliği).
const CopyField = ({ label, text, value }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className="fin-copy">
      <div className="fin-copy-text">
        <span className="fin-copy-label">{label}</span>
        <span className="fin-copy-value" title={value || ''}>{text}</span>
      </div>
      {value ? (
        <button
          type="button"
          className={`fin-copy-btn ${copied ? 'is-copied' : ''}`}
          onClick={async () => setCopied(await copyToClipboard(value))}
          aria-label={`${label} değerini kopyala`}
          title="Kopyala"
        >
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        </button>
      ) : null}
    </div>
  );
};

/**
 * Finans işleminin tüm ayrıntısını ve karar butonlarını taşıyan yan panel.
 * Liste yalnızca özet gösterir; ödeme hesabı, çevrim ölçüsü, not, dekont ve
 * onay/ret işlemleri buraya taşındı.
 *
 * Karar kuralları listeyle **ortaktır** (lib/financeDecision): panelde
 * "Onayla" gösterip listede göstermemek yöneticiye yapılamayan bir iş vaat
 * eder, backend de isteği 409 ile geri çevirir.
 */
const TransactionDetailDrawer = ({
  item,
  onClose,
  onDecision,
  onOpenReceipt,
  onReconcile,
  // Üyenin son yatırımı ve o tarihten sonraki bahis toplamı; yalnızca çekimde
  // gelir (bkz. services/principal_turnover.py). Yükleniyorken {loading},
  // istek başarısızsa {error} taşır.
  turnover = null,
  reconcileBusy = false,
  receiptBusy = false,
  // Para hareketi doğuran düğmeler finance.write ister; yetkisi olmayan
  // yönetici kaydı yine okuyabilir. Varsayılan açıktır: prop geçirmeyen eski
  // çağrılarda davranış değişmemelidir.
  canWrite = true,
  // Üstte onay penceresi açıkken Escape'i bu panel yutmamalı.
  escapeDisabled = false,
}) => {
  const titleId = useId();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!item) return undefined;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !escapeDisabled) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [escapeDisabled, item, onClose]);

  if (!item) return null;

  const Icon = iconMap[item.type] || ArrowLeftRight;
  const payout = payoutAccountView(item);
  const noteLines = transactionNoteLines(item);
  const actor = transactionActor(item);
  const ability = financeDecision(item);
  const {
    awaitingApproval, canResolve, canReconcile, locallyClosed, locallyExpired,
    manuallyResolvable, providerLabel, providerManaged,
  } = ability;
  const showDecision = canResolve && canWrite;
  const showReconcile = canReconcile && canWrite && Boolean(onReconcile);
  const localExpiryMinutes = 10;
  const turnoverStatus = turnoverView(turnover);
  const turnoverRatio = Math.max(
    0, Math.min(1, Number(turnover?.ratio ?? 0)));
  const incoming = item.amount >= 0;
  // Oyuncunun "ödemeyi yaptım" bildirimi; backend sağlayıcı gövdesinden
  // çıkarıp ayrı alan olarak verir (Hemen Havale / Hera / VEVO PAY).
  const playerPaidAt = item.player_paid_at || '';

  return createPortal((
    <div className="fin-layer admin-portal-theme" role="presentation">
      <button
        type="button"
        className="fin-backdrop"
        aria-label="Detay panelini kapat"
        onClick={onClose}
      />
      <aside
        className="fin-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="fin-drawer-head">
          <span className={`fin-drawer-icon ${item.type}`}>
            <Icon size={18} aria-hidden="true" />
          </span>
          <div className="fin-drawer-titles">
            <h2 id={titleId}>
              {transactionTypeLabel[item.type] || item.type}
              {' detayı'}
            </h2>
            <p>{item.method || 'Yöntem belirtilmedi'}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="fin-close"
            onClick={onClose}
            aria-label="Detay panelini kapat"
            title="Kapat"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="fin-drawer-body">
          <div className="fin-hero">
            <div className={`fin-hero-amount ${incoming ? 'is-in' : 'is-out'}`}>
              {incoming ? '+' : '−'}
              {formatMoney(Math.abs(item.amount))}
            </div>
            <div className="fin-hero-meta">
              <span className={`fin-badge ${stateToneMap[item.status] || 'is-gray'}`}>
                {transactionStatusLabel[item.status] || item.status}
              </span>
              <span>{formatDateTime(item.created_at)}</span>
            </div>
          </div>

          <dl className="fin-grid">
            <div>
              <dt>Kullanıcı</dt>
              <dd>{item.username}</dd>
            </div>
            <div>
              <dt>Yöntem</dt>
              <dd>{item.method || '-'}</dd>
            </div>
            <div>
              <dt>Tür</dt>
              <dd>{transactionTypeLabel[item.type] || item.type}</dd>
            </div>
            <div>
              <dt>İşlem sonrası bakiye</dt>
              <dd>
                {item.balance_after === null || item.balance_after === undefined
                  ? 'Kayıtlı değil'
                  : formatMoney(item.balance_after)}
              </dd>
            </div>
            <div>
              <dt>İşlemi yapan</dt>
              <dd className={actor ? 'is-actor' : ''}>
                {actor || 'Yetkili işlemi değil'}
              </dd>
            </div>
            <div>
              <dt>Oluşturulma</dt>
              <dd>{formatDateTime(item.created_at)}</dd>
            </div>
          </dl>

          {item.type === 'withdraw' && turnover && (
            <Section title="Anapara çevrimi">
              {turnover.loading && (
                <p className="fin-foot-note">Hesaplanıyor…</p>
              )}
              {turnover.error && (
                <p className="fin-foot-note">
                  Çevrim bilgisi alınamadı; üye profilinden bakabilirsiniz.
                </p>
              )}
              {!turnover.loading && !turnover.error && (
                <div className="fin-panel">
                  <div className="fin-panel-title">
                    <span className={`fin-badge ${turnoverToneMap[turnoverStatus.tone] || 'is-gray'}`}>
                      {turnoverStatus.label}
                    </span>
                    <small>{turnoverStatus.detail}</small>
                  </div>
                  {turnover.has_deposit && (
                    <>
                      <div className={`fin-meter ${turnover.completed ? 'is-done' : ''}`} aria-hidden="true">
                        <span style={{ width: `${Math.round(turnoverRatio * 100)}%` }} />
                      </div>
                      <dl className="fin-grid" style={{ marginTop: 10 }}>
                        <div>
                          <dt>Son yatırım</dt>
                          <dd>{formatMoney(turnover.deposit_amount)}</dd>
                        </div>
                        <div>
                          <dt>Sonrasında bahis</dt>
                          <dd title="Freebet kuponu ve ücretsiz spin çevrime sayılmaz">
                            {formatMoney(turnover.total_bet)}
                          </dd>
                        </div>
                        <div>
                          <dt>Kalan</dt>
                          <dd>{formatMoney(turnover.remaining)}</dd>
                        </div>
                        <div>
                          <dt>Yatırım tarihi</dt>
                          <dd>{formatDateTime(turnover.deposit_at)}</dd>
                        </div>
                      </dl>
                    </>
                  )}
                </div>
              )}
              {item.user_id && (
                <a
                  className="fin-foot-ghost"
                  href={`/admin/users/${item.user_id}/games`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Gamepad2 size={14} aria-hidden="true" />
                  Bahis geçmişini yeni sekmede aç
                </a>
              )}
            </Section>
          )}

          {payout && (
            <Section title="Ödeme hesabı">
              <div className="fin-panel">
                <div className="fin-panel-title">
                  {payout.title}
                  <small>{payout.subtitle}</small>
                </div>
                <CopyField label={payout.codeLabel} text={payout.codeText} value={payout.code} />
              </div>
            </Section>
          )}

          {noteLines.length > 0 && (
            <Section title="İşlem notu">
              <ul className="fin-notes">
                {noteLines.map((line, index) => (
                  <li key={`${line}-${index}`}>{line}</li>
                ))}
              </ul>
            </Section>
          )}

          {item.type === 'deposit' && item.receipt && (
            <Section title="Dekont">
              <button
                type="button"
                className="fin-foot-ghost"
                onClick={() => onOpenReceipt(item)}
                disabled={receiptBusy}
                title={item.receipt.original_name}
              >
                {receiptBusy ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <FileText size={14} aria-hidden="true" />
                )}
                {item.receipt.original_name || 'Dekontu görüntüle'}
              </button>
            </Section>
          )}

          <Section title="Kayıt">
            <CopyField label="İşlem kimliği" text={item.id} value={item.id} />
            {providerManaged && (
              <dl className="fin-grid" style={{ marginTop: 8 }}>
                <div>
                  <dt>Sağlayıcı</dt>
                  <dd>{providerLabel}</dd>
                </div>
                <div>
                  <dt>Sağlayıcı durumu</dt>
                  <dd>
                    {providerStateLabel[item.provider_status]
                      || item.provider_status
                      || '-'}
                  </dd>
                </div>
              </dl>
            )}
            {item.provider_transaction_id ? (
              <CopyField
                label="Sağlayıcı işlem no"
                text={item.provider_transaction_id}
                value={item.provider_transaction_id}
              />
            ) : null}
            {playerPaidAt ? (
              // Sağlayıcı bildirimi hiç gelmezse yöneticinin elindeki tek
              // ipucu budur: oyuncu havaleyi yaptığını söylüyor.
              <p className="fin-foot-note" style={{ marginTop: 8 }}>
                {`Oyuncu ${formatDateTime(playerPaidAt)} tarihinde ödemeyi `}
                yaptığını bildirdi. Bakiye yine de yalnızca sağlayıcının
                bildirimiyle yüklenir.
              </p>
            ) : null}
          </Section>
        </div>

        <footer className="fin-drawer-foot">
          {awaitingApproval && (
            <p className="fin-foot-note">
              <b>Talep sağlayıcıya iletilmedi.</b>
              {` Onaylarsanız ${providerLabel}'a gönderilir ve ödeme sonucunu `}
              sağlayıcı bildirir; reddederseniz tutar oyuncuya iade edilir.
            </p>
          )}
          {!awaitingApproval && canResolve && manuallyResolvable && (
            <p className="fin-foot-note">
              {`Talep sağlayıcıya iletildi ve sonucu normalde bildirim getirir. ${providerLabel}`}
              &apos;in durum sorgulama ucu olmadığı için kayıt beklemede
              kaldıysa buradan elle sonuçlandırabilirsiniz; sonradan gelen aynı
              sonuç bakiyeye ikinci kez yansımaz.
            </p>
          )}
          {canResolve && !canWrite && (
            <p className="fin-foot-note">
              Bu kayıt karara bağlanabilir ama finans yazma yetkiniz yok;
              onay ve ret düğmeleri bu yüzden çizilmedi.
            </p>
          )}
          {showDecision && (
            <>
              <button
                type="button"
                className="fin-foot-btn is-reject"
                onClick={() => onDecision('reject')}
              >
                <XCircle size={15} aria-hidden="true" />
                Reddet
              </button>
              <button
                type="button"
                className="fin-foot-btn is-approve"
                onClick={() => onDecision('approve')}
              >
                <CheckCircle2 size={15} aria-hidden="true" />
                Onayla
              </button>
            </>
          )}
          {!canResolve && showReconcile && (
            <>
              <p className="fin-foot-note">
                {locallyExpired
                  ? `Talep ${localExpiryMinutes} dakikalık ödeme süresi `
                    + 'dolduğu için otomatik iptal edildi. Bu süre '
                    + 'sağlayıcının değil bizim tahminimizdir: oyuncu son anda '
                    + 'ödediyse durumu sağlayıcıdan sorgulayın, sonuç onaylıysa '
                    + 'tutar bakiyeye yüklenir.'
                  : locallyClosed
                    ? 'Kayıt, sağlayıcı sorgusu onu bulamadığı için kapatıldı. '
                      + '"Kayıt yok" bir ret değildir: sağlayıcı işlemi '
                      + 'sonradan onaylamış olabilir, durumu yeniden '
                      + 'sorgulayın — sonuç onaylıysa tutar bakiyeye yüklenir.'
                    : `${providerLabel} işlemleri panelden onaylanamaz; sonucu `
                      + 'sağlayıcının bildirimi belirler. Kayıt beklemede '
                      + 'kaldıysa durumu sağlayıcıdan sorgulayın.'}
              </p>
              <button
                type="button"
                className="fin-foot-btn is-sync"
                onClick={() => onReconcile(item)}
                disabled={reconcileBusy}
              >
                {reconcileBusy ? (
                  <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw size={15} aria-hidden="true" />
                )}
                Sağlayıcıdan sorgula
              </button>
            </>
          )}
          {!canResolve && !showReconcile && (
            <p className="fin-foot-note">
              {item.status !== 'beklemede'
                ? `İşlem ${(transactionStatusLabel[item.status] || item.status).toLocaleLowerCase('tr-TR')}, yeni karar verilemez.`
                : canReconcile
                  ? 'Bu kayıt yalnızca sağlayıcıdan sorgulanarak çözülür; '
                    + 'finans yazma yetkiniz olmadığı için sorgulama düğmesi '
                    + 'çizilmedi.'
                  : providerManaged
                    ? `${providerLabel} ödemesi tamamlandığında bakiye `
                      + 'kendiliğinden yüklenir; yönetici onayı gerekmez. '
                      + 'Süresi dolan talep kendiliğinden iptal olur, geç gelen '
                      + 'onay yine de kredilenir.'
                    : 'Bu kayıt panelden sonuçlandırılamaz.'}
            </p>
          )}
        </footer>
      </aside>
    </div>
  ), document.body);
};

export default TransactionDetailDrawer;
